import { EMPTY, catchError, map, skip, take } from 'rxjs';
import { sendOperationalMessage } from '../graphql/messages.mutations';
import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import {
	IGqlMassMessages,
	IMassMessage,
	IMessages,
} from '../interfaces/messages.interfaces';
import { messagesQuery } from '../graphql/messages.queries';
import { Store } from '@ngrx/store';
import {
	addMessage,
	loadPaginatedData,
	setIsLoading,
	setMessages,
	setPaginationOptions,
	setViewMessage,
} from '../state/messages.action';
import { getCurrentUser } from '@app/auth/state/auth.selectors';
import { AuRightSidebarService } from '@app/core/services/au-right-sidebar.service';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@app/shared/ui-components/au-paginator/pagination-page.info';
import { selectPaginationOptions } from '../state/messages.selectors';
import { RightSidebarNavigationService } from '@app/layout/au-right-sidebar/right-sidebar-navigation.service';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import type {
	ICMMSLocation,
	LocationInput,
} from '@app/pages/cmms/interfaces/cmms-global.interfaces';

@Injectable({
	providedIn: 'root',
})
export class OperationalMessagesService {
	private readonly apollo = inject(Apollo);
	private readonly notify = inject(IconSnackBarService);
	private readonly store$ = inject(Store);
	private readonly sidebarService = inject(AuRightSidebarService);
	private readonly sidebarNavigation = inject(RightSidebarNavigationService);

	sendOperationalMessage(form: IMassMessage, location: LocationInput) {
		const emails = form.recipients
			.toLocaleLowerCase()
			.split(',')
			.map(email => email.trim());

		const attachmentPks = form.files.map(file => file.pk);

		return this.apollo
			.mutate({
				mutation: sendOperationalMessage,
				fetchPolicy: 'no-cache',
				variables: {
					subject: form.subject,
					text: form.text,
					recipients: emails,
					locations: location && [location],
					attachmentPks: attachmentPks,
				},
			})
			.pipe(
				take(1),
				catchError(() => {
					this.notify.error('Could not send message');
					return EMPTY;
				})
			);
	}

	messagesQuery(
		locations: string,
		paginationVariables: IAuPaginationData = { first: 10 }
	) {
		return this.apollo
			.query<IMessages>({
				query: messagesQuery,
				variables: {
					...paginationVariables,
					locations: locations && [locations],
				},
				fetchPolicy: 'no-cache',
			})
			.pipe(
				take(1),
				map(res => {
					const messages = res?.data?.tenantMassMessages;
					return {
						messages: messages.edges.map(edge => edge.node),
						messagesPagination: {
							pageInfo: messages.pageInfo,
							totalCount: messages.totalCount,
						},
					};
				})
			);
	}

	set(messages: IGqlMassMessages[], messagesPagination: IDjangoPaginationData) {
		this.store$.dispatch(setMessages({ messages, messagesPagination }));
	}

	add(messages: IMassMessage, recipients: string) {
		messages.recipients = recipients;
		this.store$
			.select(getCurrentUser)
			.pipe(take(1))
			.subscribe(currentUser => {
				this.store$.dispatch(addMessage({ messages, currentUser: currentUser }));
			});
	}

	openAddMessageWorkspace(
		recipients: { name: string; email: string }[] = [],
		missingEmails = 0,
		location?: Partial<ICMMSLocation> | null
	) {
		this.sidebarService.setComponentAndOpen(ACTIVE_PAGE_CONFIG.Messages.createComponent, {
			...ACTIVE_PAGE_CONFIG.Messages.sidebarConfig,
			initialContent: { recipients, missingEmails, location },
		});
	}

	handlePaginationUpdateEvent(paginationData: IAuPaginationData) {
		this.store$.dispatch(setIsLoading({ isLoading: true }));
		this.store$
			.select(selectPaginationOptions)
			.pipe(skip(1), take(1))
			.subscribe(_ => {
				this.store$.dispatch(loadPaginatedData());
			});
		this.setPagination(paginationData);
	}

	setPagination(paginationData: IAuPaginationData) {
		this.store$.dispatch(setPaginationOptions({ paginationOptions: paginationData }));
	}

	openMessageInWorkspace(message: IGqlMassMessages) {
		this.store$.dispatch(setViewMessage({ viewMessage: message }));

		this.sidebarNavigation.navigateTo(CmmsPages.messages, {
			title: {
				text: 'message',
				endfix: message.subject,
			},
			showContentActions: false,
		});
	}
}
